import { ofType } from 'redux-observable';
import { merge, partition } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { getShortlistedVenuesList } from '@bridebook/web/lib/shortlist/selectors';
import {
  IApplicationState,
  IEpic,
  TUIShortlistSupplier,
  VenueConfirmSlides,
} from '@bridebook/web/lib/types';
import {
  VenueConfirmActionTypes,
  VenueConfirmChangeSlideAction,
} from '@bridebook/web/lib/venue-confirmation/action-types';
import {
  closeBookingConfirmationModal,
  saveSupplierIdToConfirm,
  venueConfirmChangeSlide,
} from '@bridebook/web/lib/venue-confirmation/actions';
import { triggerSupplierInterestConfirmationAnalytics } from '@bridebook/web/lib/venue-confirmation/analytics/actions';
import { withMarket } from '@bridebook/web/lib/utils/operators/with-market';
import { LostLeadService } from '@bridebook/web/lib/lost-lead/lost-lead-service';

type InputActions = VenueConfirmChangeSlideAction;

/**
 * An Epic that handles the logic for showing the supplier interest confirmation modal.
 */
export const openSupplierInterestConfirmationEpic: IEpic<InputActions, any> = (
  action$,
  { state$, lostLeadService },
) => {
  const [hasAvailableSupplier$, hasNoAvailableSupplier$] = partition(
    action$.pipe(
      ofType(VenueConfirmActionTypes.TRIGGER_SUPPLIER_CONFIRMATION),
      withMarket(state$),
      map(({ market, input, state }) => ({
        input,
        state,
        market,
        availableSupplier: getAvailableVPESupplier(state, lostLeadService),
      })),
    ),
    ({ availableSupplier }) => !!availableSupplier,
  );

  return merge(
    hasAvailableSupplier$.pipe(
      map(({ availableSupplier }) => {
        // At this point, we know availableSupplier is not null due to the partition
        const supplier = availableSupplier as NonNullable<typeof availableSupplier>;
        return supplier.id;
      }),
      mergeMap((supplierId) => [
        saveSupplierIdToConfirm(supplierId),
        venueConfirmChangeSlide(VenueConfirmSlides.supplierInterest),
        triggerSupplierInterestConfirmationAnalytics(supplierId),
      ]),
    ),
    hasNoAvailableSupplier$.pipe(
      mergeMap(() => [
        toggleSnackbar('success', getI18n().t('venueConfirmation:happyHunting')),
        closeBookingConfirmationModal(),
      ]),
    ),
  );
};

/**
 * Gets the first available VPE supplier that should be shown based on lostLeadService logic.
 */
const getAvailableVPESupplier = (
  state: IApplicationState,
  lostLeadService: LostLeadService,
): TUIShortlistSupplier | null => {
  const shortlistedSuppliers = getShortlistedVenuesList(state) || [];

  // Find the first supplier that meets the lost lead criteria
  for (const supplier of shortlistedSuppliers) {
    const isAvailable =
      supplier &&
      lostLeadService.checkAvailability(
        {
          supplierId: supplier.id,
          supplierTier: supplier.tier,
          supplierType: supplier.type,
          countryCode: supplier.countryCode || supplier.l10n?.country,
        },
        supplier,
      );

    if (isAvailable) {
      return supplier;
    }
  }

  return null;
};
